import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useSafeThemeClasses } from '@/shared/theme';
import { useMediaQuery } from '@/shared/hooks';
import { ROUTES } from '@/shared/config';
import { PAGE_KEYS } from '@/shared/config/pageKeys';
import { useUserState } from '@/features/user/operations/UserStateProvider';
import AdminUserManagementPage from './AdminUserManagementPage';
import AdminBlogManagementPage from './AdminBlogManagementPage';
import AdminAnalyticsPage from './AdminAnalyticsPage';
import AdminPortfolioPage from './AdminPortfolioPage';
import AdminOverviewPage from './AdminOverviewPage';
import AdminErrorsPage from './AdminErrorsPage';
import AdminActivityPage from './AdminActivityPage';
import AdminEntitlementsPage from './AdminEntitlementsPage';

type AdminSection = 'overview' | 'users' | 'blogs' | 'analytics' | 'portfolio' | 'errors' | 'activity' | 'entitlements';

// This tab UI is a second access surface for the exact same pages routes.tsx
// already gates - it renders admin pages directly, bypassing ProtectedRoute
// entirely. Without this map+filter, a page denied via its standalone route
// would still be fully reachable as a tab here.
const SECTION_PAGE_KEYS: Record<AdminSection, string> = {
  overview: PAGE_KEYS.ADMIN_DASHBOARD,
  users: PAGE_KEYS.ADMIN_USERS,
  blogs: PAGE_KEYS.ADMIN_BLOGS,
  analytics: PAGE_KEYS.ADMIN_ANALYTICS,
  portfolio: PAGE_KEYS.ADMIN_PORTFOLIO,
  activity: PAGE_KEYS.ADMIN_ACTIVITY,
  errors: PAGE_KEYS.ADMIN_ERRORS,
  entitlements: PAGE_KEYS.ADMIN_ENTITLEMENTS,
};

const SECTION_ICON_PATHS: Record<AdminSection, string> = {
  overview: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  blogs: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  analytics: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  portfolio: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5',
  activity: 'M13 10V3L4 14h7v7l9-11h-7z',
  errors: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  entitlements: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
};

const AdminDashboardPage: React.FC = () => {
  const theme = useSafeThemeClasses();
  const { hasEntitlement } = useUserState();
  const [activeSection, setActiveSection] = useState<AdminSection>('overview');
  // Desktop-only permanent rail - hidden entirely below lg: (mobile uses the
  // tab row instead, so there's no off-canvas drawer/second hamburger
  // stacked under the global nav's own hamburger). Tailwind v4 + DaisyUI's
  // compiled CSS doesn't reliably let translate-x utility classes win over
  // each other for the standalone `translate` property here, so the
  // transform is still driven inline rather than via classes.
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const selectSection = (section: AdminSection) => {
    setActiveSection(section);
  };

  const allSections: Array<{ id: AdminSection; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'blogs', label: 'Blogs' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'activity', label: 'Activity' },
    { id: 'errors', label: 'Errors' },
    { id: 'entitlements', label: 'Entitlements' },
  ];
  const sections = useMemo(
    () => allSections.filter((s) => hasEntitlement(SECTION_PAGE_KEYS[s.id])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasEntitlement]
  );

  // If the current (possibly default) section got denied, fall back to
  // whatever's actually visible rather than rendering a blank tab.
  useEffect(() => {
    if (sections.length === 0) return;
    if (!sections.some((s) => s.id === activeSection)) {
      setActiveSection(sections[0].id);
    }
  }, [sections, activeSection]);

  return (
    <div className={`flex min-h-screen transition-opacity duration-500 ${theme.background} pb-20`}>
      {/* Background decoration */}
      <div className='fixed inset-0 overflow-hidden pointer-events-none'>
        <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]} animate-pulse`}></div>
        <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]} animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Sidebar is portaled to <body> - position:fixed breaks out of the
          viewport when nested inside a backdrop-blur/transform ancestor
          (each of those establishes a new containing block per the CSS
          spec), which was pushing the sidebar fully off-screen and
          unreachable. It's desktop-only (lg:); on mobile, section switching
          happens via the tab row below instead of a second hamburger-
          triggered drawer, so there's only ever one menu control on screen
          (the global nav's) at once. */}
      {createPortal(
      <div
        className={`fixed top-[4.8rem] left-0 h-[calc(100%-4.8rem)] w-64 border-r z-20 transition-all duration-300 ease-in-out ${theme.sidebar}`}
        style={{ transform: isDesktop ? 'translateX(0)' : 'translateX(-100%)' }}
      >
        {/* Header */}
        <div className={`p-5 flex justify-between items-center border-b ${theme.separator}`}>
          <h2 className={`font-bold text-xl ${theme.textHeading} tracking-tight`}>Admin</h2>
        </div>

        {/* Navigation Menu */}
        <nav className="p-4 space-y-1.5">
          {/* Back to Dashboard */}
          <Link
            to={ROUTES.DASHBOARD}
            className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className={`ml-3 font-medium transition-opacity duration-300`}>Dashboard</span>
          </Link>

          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => selectSection(section.id)}
              className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${
                activeSection === section.id
                  ? `${theme.navItemActive} shadow-lg scale-[1.02]`
                  : `${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={SECTION_ICON_PATHS[section.id]} />
              </svg>
              <span className={`ml-3 font-medium transition-opacity duration-300`}>{section.label}</span>
            </button>
          ))}
        </nav>
      </div>,
        document.body
      )}

      {/* Main Content Area - sidebar is permanently visible from lg: up, so
          content always reserves space for it there regardless of the
          mobile-only open/closed toggle state. min-w-0 is load-bearing: this
          is a flex item, and flex items default to min-width:auto, meaning
          they refuse to shrink below their content's intrinsic width. The
          mobile tab row below it (8 non-wrapping pills, overflow-x-auto)
          has a wide intrinsic content width - without min-w-0 that width
          bubbled up through every block ancestor here instead of staying
          contained by the tab row's own scroll, pushing the entire page
          wider than the viewport on mobile. */}
      <div className="relative flex-1 min-w-0 transition-all duration-300 ml-0 lg:ml-64">
        {/* Top Bar - Page title (no toggle button: the global nav already owns
            the one hamburger on this page; mobile section switching happens
            via the tab row directly below) */}
        <div className={`sticky top-0 z-10 ${theme.header}`}>
          <div className="px-6 py-4">
            <h1 className={`text-2xl font-bold text-center ${theme.textHeading} tracking-tight`}>Admin Dashboard</h1>
          </div>

          {/* Mobile section tabs - replaces the old off-canvas drawer on small
              screens so there's a single, always-visible way to switch
              sections instead of a redundant second hamburger. */}
          <nav className="lg:hidden flex gap-2 overflow-x-auto px-4 pb-3 -mt-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => selectSection(section.id)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeSection === section.id ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` : `${theme.buttonGhost} ${theme.textSecondary}`
                }`}
              >
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content - Glass Morphism. This used to be its own fixed-height,
            internally-scrolling region (h-[calc(100vh-80px)] + a nested
            overflow-y-auto pageWrapper) stacked below the sticky top bar.
            That created two independent scroll contexts on one page: this
            inner one, and the outer document (whose real height always
            exceeded the hardcoded 80px offset once the global nav/footer
            were counted, since neither is 80px). With two scrollers, the
            page's ambient background decoration - which lives on the
            outer, document-scrolling container - moved out of sync with
            this glass panel as you scrolled it, showing through as a
            stray second background on any section tall enough to scroll
            (Analytics, Blogs, Entitlements, ...). Letting this panel scroll
            with the normal document instead (no fixed height, no its own
            overflow) removes the second scroll context entirely; the sticky
            top bar and the portaled fixed sidebar don't need it. */}
        <div className="p-6 md:p-8 lg:p-10 animate-fadeIn">
          <div className={`${theme.pageWrapper} p-6 md:p-8 lg:p-10`}>
            <div className="relative z-10">
              {activeSection === 'overview' && <AdminOverviewPage onNavigate={selectSection} />}
              {activeSection === 'users' && <AdminUserManagementPage />}
              {activeSection === 'blogs' && <AdminBlogManagementPage />}
              {activeSection === 'analytics' && <AdminAnalyticsPage />}
              {activeSection === 'portfolio' && <AdminPortfolioPage />}
              {activeSection === 'activity' && <AdminActivityPage />}
              {activeSection === 'errors' && <AdminErrorsPage />}
              {activeSection === 'entitlements' && <AdminEntitlementsPage />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;

